import React from "react";
import { generatePath, useNavigate } from "react-router-dom";
import {
  AutocompleteChangeReason,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  createFilterOptions,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import DesignStateService from "../../service/DesignStateService";
import { ROUTES } from "../../constant/common.constant";
import { palette } from "../../styles/theme";
import { useSpecsEditorContextValue } from "./useSpecsEditor";
import { useSpecsRevisionHistory } from "./useSpecsRevisionHistory";
import { toast } from "react-toastify";

type ImportProductOption = {
  name: string;
  displayName: string;
  searchText: string;
  skuPrextra: string;
  deprecated: boolean;
};

const NEW_PRODUCT_FROM_SCRATCH_OPTION: ImportProductOption = {
  name: "__new_product_from_scratch__",
  displayName: "New product from scratch",
  searchText: "new product from scratch",
  skuPrextra: "",
  deprecated: false,
};

type ImportSpecsProduct = {
  name?: string;
  displayName?: string;
  versions?: Array<{
    name?: string;
    skuPrextra?: string;
    status?: string;
  }>;
};

function getLatestProductVersion(product: ImportSpecsProduct) {
  const versions = product.versions || [];
  return versions.length > 0 ? versions[versions.length - 1] : null;
}

function getLatestProductPrextraSku(product: ImportSpecsProduct) {
  const latestVersion = getLatestProductVersion(product);
  return typeof latestVersion?.skuPrextra === "string"
    ? latestVersion.skuPrextra.trim()
    : "";
}

function isLatestProductVersionDeprecated(product: ImportSpecsProduct) {
  return getLatestProductVersion(product)?.status === "deprecated";
}

function trimProductLabel(value: string) {
  return value.length > 20 ? value.slice(0, 20) : value;
}

export function buildImportProductOption(
  product: ImportSpecsProduct
): ImportProductOption {
  const name = product.name || "";
  const baseDisplayName = product.displayName || name;
  const trimmedDisplayName = trimProductLabel(baseDisplayName);
  const skuPrextra = getLatestProductPrextraSku(product);
  const deprecated = isLatestProductVersionDeprecated(product);
  const productDisplayName = deprecated
    ? `${trimmedDisplayName} [deprecated]`
    : trimmedDisplayName;
  const displayName = skuPrextra
    ? `${productDisplayName} - ${skuPrextra}`
    : productDisplayName;

  return {
    name,
    displayName,
    searchText: `${baseDisplayName} ${name} ${skuPrextra} ${
      deprecated ? "deprecated" : ""
    }`.toLowerCase(),
    skuPrextra,
    deprecated,
  };
}

export const filterImportProductOptions =
  createFilterOptions<ImportProductOption>({
    stringify: (option) => option.searchText,
    trim: true,
  });

export function orderImportProductOptions(options: ImportProductOption[]) {
  return [...options].sort((left, right) => {
    if (left.deprecated === right.deprecated) {
      return 0;
    }

    return left.deprecated ? 1 : -1;
  });
}

const DEFAULT_PREVIEW_FAMILY = {
  name: "hoodie",
  version: "1.0",
};

export default function SpecsToolbar() {
  const navigate = useNavigate();
  const {
    loadedRevisionId,
    historySaving,
    historyPublishing,
    historyValidating,
    handleSaveDraftRevision,
    handleSaveAndPublishRevision,
    handleValidateRevision,
  } = useSpecsRevisionHistory();
  const {
    editor,
    importSpecs,
    importLoading,
    importSelectedProduct,
    importSelectedVersion,
    setImportSelectedProduct,
    setImportSelectedVersion,
    openImportDialog,
    modals,
  } = useSpecsEditorContextValue();
  const [previewLaunching, setPreviewLaunching] = React.useState(false);
  const [productInputValue, setProductInputValue] = React.useState("");

  const hasSessionProductIdentity =
    !!editor.identity.productName && !!editor.identity.productVersion;
  const canSaveRevision = !!importSpecs && hasSessionProductIdentity;
  const canPreviewInStudio = !!loadedRevisionId;

  const getVersionsForProduct = (productName: string) =>
    importSpecs?.products?.find((product: any) => product.name === productName)
      ?.versions ?? [];

  const importProducts = React.useMemo(
    () =>
      importSpecs?.products?.map((product: ImportSpecsProduct) =>
        buildImportProductOption(product)
      ) ?? [],
    [importSpecs]
  );
  const importVersions = React.useMemo(
    () =>
      importSpecs?.products
        ?.find((product: any) => product.name === importSelectedProduct)
        ?.versions?.map((version: any) => version.name) ?? [],
    [importSelectedProduct, importSpecs]
  );
  const productOptions = React.useMemo(
    () => [
      NEW_PRODUCT_FROM_SCRATCH_OPTION,
      ...orderImportProductOptions(importProducts),
    ],
    [importProducts]
  );

  React.useEffect(() => {
    if (!importSelectedProduct) {
      setProductInputValue("");
      return;
    }

    const selectedProduct = productOptions.find(
      (product) => product.name === importSelectedProduct
    );
    setProductInputValue(selectedProduct?.displayName ?? "");
  }, [importSelectedProduct, productOptions]);

  const handleSelectNewProductFromScratch = React.useCallback(() => {
    editor.clearEditorState();
    navigate(ROUTES.SPECS_GENERATOR, { replace: true });
  }, [editor, navigate]);

  const handlePreviewInStudio = React.useCallback(async () => {
    if (
      previewLaunching ||
      !importSpecs ||
      !hasSessionProductIdentity ||
      !loadedRevisionId
    ) {
      return;
    }

    const family = DEFAULT_PREVIEW_FAMILY;

    setPreviewLaunching(true);
    try {
      const previewProduct = {
        name: editor.identity.productName,
        version: editor.identity.productVersion,
        productLine:
          typeof editor.rawVersionMetadata?.productLine === "string"
            ? editor.rawVersionMetadata.productLine
            : "",
      };

      const createDesignResponse = await DesignStateService.createDesignState({
        family,
        product: previewProduct,
        specRevisionId: loadedRevisionId,
      });
      const designId = createDesignResponse?.data?.designId;
      if (!designId) {
        throw new Error("Preview design id was not returned");
      }

      const studioPath = generatePath(ROUTES.SPECS_STUDIO_PREVIEW, {
        designId,
      });
      window.open(studioPath, "_blank", "noopener,noreferrer");
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to preview in Studio"
      );
    } finally {
      setPreviewLaunching(false);
    }
  }, [
    editor,
    hasSessionProductIdentity,
    importSpecs,
    loadedRevisionId,
    previewLaunching,
  ]);

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      sx={{
        px: 2,
        py: 1,
        borderBottom: `1px solid ${palette.grey.lightGrey}`,
        bgcolor: palette.grey.lightBackground,
        flexShrink: 0,
      }}
    >
      <FormControl variant="standard" size="small" sx={{ minWidth: 300 }}>
        <Autocomplete
          sx={{ pt: "0 !important" }}
          size="small"
          openOnFocus={false}
          options={productOptions}
          filterOptions={filterImportProductOptions}
          getOptionLabel={(option) => option.displayName}
          inputValue={productInputValue}
          value={
            productOptions.find(
              (product) => product.name === importSelectedProduct
            ) || null
          }
          onInputChange={(_event, newInputValue) => {
            setProductInputValue(newInputValue);
          }}
          onChange={(
            _event,
            newValue: ImportProductOption | null,
            _reason: AutocompleteChangeReason
          ) => {
            if (newValue?.name === NEW_PRODUCT_FROM_SCRATCH_OPTION.name) {
              setProductInputValue("");
              handleSelectNewProductFromScratch();
              return;
            }

            const productName = newValue ? newValue.name : "";
            setImportSelectedProduct(productName);
            const versions = getVersionsForProduct(productName);
            const nextVersion = versions[versions.length - 1]?.name ?? "";
            setImportSelectedVersion(nextVersion);
          }}
          onOpen={openImportDialog}
          loading={importLoading}
          renderOption={(props, option) => (
            <Box
              component="li"
              {...props}
              sx={{
                color: option.deprecated ? "text.secondary" : "text.primary",
              }}
            >
              {option.displayName}
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="standard"
              placeholder="Select product..."
              autoComplete="off"
              inputProps={{
                ...params.inputProps,
                autoComplete: "off",
                "data-1p-ignore": true,
                "data-lpignore": "true",
                "data-form-type": "other",
              }}
              InputProps={{
                ...params.InputProps,
                disableUnderline: true,
                endAdornment: (
                  <>
                    {importLoading ? <CircularProgress size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </FormControl>

      <FormControl variant="standard" size="small" sx={{ minWidth: 60 }}>
        <Select
          value={importSelectedVersion}
          onChange={(event) => setImportSelectedVersion(event.target.value)}
          disableUnderline
          disabled={!importSelectedProduct}
          displayEmpty
          renderValue={(value) => value || "v"}
        >
          {importVersions.map((version: string) => (
            <MenuItem key={version} value={version}>
              {version}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <IconButton
        size="small"
        onClick={modals.openDuplicateProductDialog}
        disabled={!hasSessionProductIdentity}
        data-test="specsEditor.duplicateProduct.open"
      >
        <FileCopyIcon fontSize="small" />
      </IconButton>

      <Box sx={{ flex: 1 }} />

      <Tooltip
        title={
          canPreviewInStudio
            ? "Open preview in Studio"
            : "Sauvegarder le design pour prévisualiser"
        }
      >
        <span>
          <IconButton
            size="small"
            onClick={handlePreviewInStudio}
            disabled={!canPreviewInStudio || previewLaunching}
            data-test="specsEditor.previewInStudio.open"
          >
            {previewLaunching ? (
              <CircularProgress size={16} />
            ) : (
              <OpenInNewIcon fontSize="small" />
            )}
          </IconButton>
        </span>
      </Tooltip>

      <Button
        variant="outlined"
        size="small"
        onClick={handleValidateRevision}
        disabled={!canSaveRevision || historyValidating}
      >
        {historyValidating ? "Validation..." : "Valider"}
      </Button>

      <Button
        variant="outlined"
        size="small"
        onClick={handleSaveDraftRevision}
        disabled={!canSaveRevision || historySaving || historyPublishing}
      >
        {historySaving ? "Sauvegarde..." : "Sauvegarder"}
      </Button>

      <Button
        variant="contained"
        size="small"
        onClick={handleSaveAndPublishRevision}
        disabled={!canSaveRevision || historySaving || historyPublishing}
      >
        {historyPublishing ? "Publication..." : "Publier"}
      </Button>
    </Stack>
  );
}
